/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useEffect, useState } from 'react'
import { ChildServiceSelection } from '../../Referral/AddChildModal/ChildServiceSelection'
import { type childServiceSettings } from '../../../ContextProviders/AddChildProvider'
import { sendGet, sendPost, sendPut } from '../../../hooks/use-fetch'
import { type isdServiceProvider, type program, type postError, type service, type carenetworkService, type lookup, Role } from '../../../core/types'
import { toast } from 'react-toastify'
import { CircularProgress } from '@mui/material'
import { AcceptDenyModal } from '../../../Components/Modal/AcceptDenyModal'
import { ServiceSummaryCard } from '../../../Components/Services/ServiceSummaryCard'
import { useAuth } from '../../../hooks/use-auth'

export interface CareNetworkServiceDetailsProps {
  service: carenetworkService
  isNew: boolean
  childID: number
  isdid: number
  districtId: number
  open: boolean
  onClose: () => void
  onSubmit: (newSettings?: childServiceSettings) => void
  onExit: () => void
  childName?: string
  careNetworkServices: carenetworkService[]
  overrideSubmit?: boolean
  errorContent?: JSX.Element
}

export function CareNetworkServiceDetails (props: CareNetworkServiceDetailsProps): JSX.Element {
  const { user } = useAuth()
  const [loaded, setLoaded] = useState(false)
  const [serviceProviders, setServiceProviders] = useState<isdServiceProvider[]>([])
  const [serviceList, setServiceList] = useState<Array<{ id: number, name: string, s3Key: string, inactive: boolean, serviceId: number }>>([])
  const [programList, setProgramList] = useState<Array<{ id: number, name: string, allowConcurrentEnrollment: boolean, requiresIncomeCalculation: boolean, programId: number }>>([])
  const [facilities, setFacilities] = useState<Array<{ id: number, name: string }>>([])
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false)
  const [isUpdateDisabled, setIsUpdateDisabled] = useState<boolean>(false)
  const [dateRangeList, setDateRangeList] = useState<lookup[]>([])
  const [serviceSettings, setServiceSettings] = useState<childServiceSettings>({
    referralId: 0,
    sessionBlockId: 0,
    desiredSchedule: props.service.desiredSchedule,
    sessionBlockName: props.service.sessionBlockName,
    timeFrame: props.service.timeFrameName,
    customDate: props.service.customDate,
    facilityId: props.service.facilityID,
    notes: props.service.notes,
    income: null,
    incomeVerified: null,
    povertyLevel: null,
    povertyPercent: null,
    povertyPercentCutOff: null,
    memberCount: null,
    program: {
      id: props.service.programID, requiresIncomeCalculation: props.service.requiresIncomeCalculation, programId: props.service.isdServiceProviderProgramID
    } as program,
    service: { isdid: props.isdid, id: props.service.serviceID, name: props.service.serviceName, serviceId: props.service.isdServiceProviderServiceID } as service,
    serviceProviderId: props.service.serviceProviderID,
    isdId: props.isdid,
    districtId: props.districtId,
    facilityName: props.service.facilityName?.toString() ?? '',
    serviceProviderName: props.service.serviceProviderName,
    status: props.service.status,
    timeFrameId: null
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response } = await sendGet(`/CareNetworkEdit/GetAllProviders?isdid=${props.isdid}`, {})
      if (response != null && response.length > 0) {
        setServiceProviders(response)
      }
      if (serviceSettings.serviceProviderId !== null && !props.isNew) {
        const { response: rsp } = await sendGet(`/CareNetworkEdit/GetServices?id=${serviceSettings.serviceProviderId}`, {})
        if (rsp != null && rsp.length > 0) {
          setServiceList(rsp)
        }
        const { response: rsp1 } = await sendGet(`/CareNetworkEdit/GetPrograms?id=${serviceSettings.serviceProviderId}`, {})
        if (rsp1 != null && rsp1.length > 0) {
          setProgramList(rsp1)
        }
      }

      const { response: rsp3 } = await sendGet(`/ReferralEdit/GetDateRangesByReferral?referralID=${-1}`, {})
      if (rsp3 !== undefined && rsp3.length > 0) {
        setDateRangeList(rsp3)
      } else {
        setDateRangeList([])
      }

      setLoaded(true)
    }
    void fetchData()
  }, [])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if ((serviceSettings.status === 'Pending' || serviceSettings.status === 'Waiting') && serviceSettings.serviceProviderId != null) {
        void GetFacilities(serviceSettings.program?.id ?? 0)
      }
    }
    void fetchData()
  }, [serviceSettings.timeFrame, serviceSettings.customDate, serviceSettings.program?.id])

  const GetFacilities = async (prgmId: number): Promise<void> => {
    const { response: rsp } = await sendGet(`/CareNetworkEdit/GetFacilities?isdid=${props.isdid.toString()}&providerId=${String(serviceSettings.serviceProviderId ?? 0)}&programID=${prgmId}`, {})
    setFacilities(rsp)
  }

  const handleClose = (): void => {
    setIsUpdateDisabled(false)
    props.onClose()
  }

  const handleSubmit = async (): Promise<void> => {
    if (!isRequestLoading) {
      setIsRequestLoading(true)
      const data = {
        serviceSettings,
        childID: props.childID
      }
      if (props.overrideSubmit === true) {
        props.onSubmit(serviceSettings)
      } else {
        if (props.isNew) {
          const { response } = await sendPost('/CareNetworkEdit/AddService', data)
          const rsp: { response: any, success: boolean, errors: postError[] } = response
          if (rsp.success) {
            toast.success('Service added successfully')
            props.onSubmit()
          } else {
            rsp.errors.filter(e => e.group === 'services').map((err) => toast.error(err.error, { position: 'top-right' }))
          }
        } else {
          const { response } = await sendPut('/CareNetworkEdit/UpdateService', data)
          const rsp: { response: any, success: boolean, errors: postError[] } = response
          if (rsp.success) {
            toast.success('Service updated successfully')
            props.onSubmit()
          } else {
            rsp.errors.filter(e => e.group === 'services').map((err) => toast.error(err.error, { position: 'top-right' }))
          }
        }
      }
      setIsRequestLoading(false)
    }
  }

  const handleServiceChange = (id: string): void => {
    const intVal = parseInt(id)
    if (intVal === serviceSettings.service?.id) return
    const service = serviceList.find(p => p.id === intVal)
    const newSettings: childServiceSettings = {
      ...serviceSettings,
      service: {
        id: intVal,
        isdid: props.isdid,
        inactive: false,
        name: '',
        description: '',
        s3Key: '',
        serviceId: service?.serviceId ?? 0
      },
      program: null
    }
    setServiceSettings(newSettings)
  }

  const handleProviderChange = async (id: string): Promise<void> => {
    const intVal = parseInt(id)
    const newSettings: childServiceSettings = {
      ...serviceSettings,
      serviceProviderId: intVal,
      service: null,
      program: null
    }
    setServiceSettings(newSettings)

    const { response: rsp } = await sendGet(`/CareNetworkEdit/GetServices?id=${id}`, {})
    if (rsp !== undefined && rsp.length > 0) {
      setServiceList(rsp)
    } else {
      setServiceList([])
    }
    const { response: rsp2 } = await sendGet(`/CareNetworkEdit/GetPrograms?id=${id}`, {})
    if (rsp2 !== undefined && rsp2.length > 0) {
      setProgramList(rsp2)
    } else {
      setProgramList([])
    }
  }
  const handleTimeFrameChange = async (timeFrame: string): Promise<void> => {
    const timeFrameId = parseInt(timeFrame)
    const existingService = props.careNetworkServices.find(s =>
      s.status !== 'Exited' &&
      s.status !== 'Cancelled' &&
      s.status !== 'Denied' &&
      s.status !== 'Transferred' &&
      !s.allowConcurrent &&
      s.timeFrameID === timeFrameId
    )
    const newSettings = {
      ...serviceSettings,
      timeFrameId
    }
    setServiceSettings(newSettings)
    const program = programList.find(p => p.id === serviceSettings.program?.id)
    if (program?.allowConcurrentEnrollment === false && existingService !== undefined) {
      if (user?.securityLevel === Role.SERVICE_PROVIDER_USER) {
        toast.error('You are not allowed to create multiple enrollments for this program. You will need to initiate a transfer based on the current settings.')
        setIsUpdateDisabled(true)
      } else {
        toast.warning('You are not allowed to create multiple enrollments for this program within the same school year. You can create this referral, but it will be added to the child\'s waitlist.')
      }
    }
    setIsUpdateDisabled(false)
  }
  const handleProgramChange = async (prgmId: string): Promise<void> => {
    const intVal = parseInt(prgmId)
    if (intVal === serviceSettings.program?.id) return
    void GetFacilities(intVal)
    const program = programList.find(p => p.id === intVal)
    setIsUpdateDisabled(false)
    const newSettings = {
      ...serviceSettings,
      program: {
        id: intVal, requiresIncomeCalculation: program?.requiresIncomeCalculation, programId: program?.programId
      } as program,
      desiredSchedule: null,
      facilityId: null,
      timeFrame: null
    }
    setServiceSettings(newSettings)
  }

  const titleContent = <div className="d-flex f-align-items-center">
    <span style={{ marginRight: '15px' }}>Service Details - {props.childName}</span>
  </div>

  const isEnrolled = props.service.type === 'enrollment'
  const isExited = props.service.status === 'Exited'
  const isOpenReferral = props.service.status === 'Pending' || props.service.status === 'Waiting' || props.service.status === 'Waiver'

  const content = loaded
    ? isOpenReferral
      ? <ChildServiceSelection
          status={props.service.status}
          providerList={serviceProviders}
          serviceList={serviceList}
          programList={programList}
          facilityList={facilities}
          dateRangeList={dateRangeList}
          serviceSettings={serviceSettings}
          onProgramChange={handleProgramChange}
          onServiceChange={handleServiceChange}
          onTimeFrameChange={handleTimeFrameChange}
          onServiceProviderChange={handleProviderChange}
          setServiceSettings={setServiceSettings}
          isNew={props.isNew}
          showIncome={serviceSettings.program?.requiresIncomeCalculation ?? false}
      />
      : <ServiceSummaryCard
          buildingName={props.service.facilityName ?? ''}
          blockName={props.service.sessionBlockName ?? '' }
          sessionName={props.service.sessionName ?? '' }
          serviceLogo={props.service.serviceLogo ?? null}
          serviceProviderLogo={props.service.serviceProviderLogo ?? null}
          programName={props.service.programName}
          referralTimeFrame={props.service.timeFrameName ?? ''}
          serviceName={props.service.serviceName}
          serviceProviderName={props.service.serviceProviderName}
          serviceStatus={props.service.status}
      />
    : <CircularProgress />
  const fullContent = <>{props.errorContent}
    {content}
  </>

  if (props.childID === undefined) return <></>

  return <AcceptDenyModal
    id={'serviceModal'}
    open={props.open}
    titleContent={titleContent}
    maxWidth='sm'
    fullWidth={true}
    dialogContent={ fullContent }
    isUpdateDisabled={isUpdateDisabled}
    onClose={handleClose}
    acceptButtonText={props.isNew ? 'Submit' : 'Update'}
    denyButtonText='Exit'
    cancelButtonText={isOpenReferral ? 'Discard Changes' : 'Cancel'}
    onAccept={isOpenReferral ? handleSubmit : undefined}
    onDeny={isEnrolled && !isExited ? props.onExit : undefined}
  />
}
